/**
 * Centralized Axios API Client for HeatCraftHP
 * 
 * SECURITY FEATURES:
 * - Access token stored in memory only (not localStorage) - prevents XSS token theft
 * - Refresh token stored in httpOnly cookie (sent automatically by browser)
 * - CSRF protection via double-submit cookie pattern
 * - Automatic token refresh on 401
 * 
 * Features:
 * - Automatic JWT token management
 * - Token refresh on 401
 * - Request/Response interceptors
 * - Typed error handling
 * - Request cancellation support
 * - Upload progress tracking
 */

import axios from 'axios';
import { ApiError, UnauthorizedError, RequestCancelledError } from './apiErrors';

// ======================
// Configuration
// ======================
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_REFRESH_ENDPOINT = '/auth/refresh';
const REQUEST_TIMEOUT = 30000; // 30 seconds default
const CONFIRMED_SESSION_KEY = 'heatcraft:auth:confirmed-session';

// Auth endpoints that should NOT trigger re-auth modal on 401/403
// These are authentication operations where errors should be shown directly
const AUTH_ENDPOINTS_SKIP_REAUTH = [
  '/auth/login',
  '/auth/register',
  '/auth/2fa/verify',
  '/auth/2fa/recovery-login',
  '/auth/forced-password-change',
  '/auth/session-restore',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/confirm-email',
  '/auth/resend-confirmation',
];

/**
 * Check if a request URL is an auth endpoint that should skip re-auth modal
 * @param {string} url - Request URL
 * @returns {boolean} True if should skip re-auth modal
 */
const shouldSkipReAuth = (url) => {
  if (!url) return false;
  return AUTH_ENDPOINTS_SKIP_REAUTH.some(endpoint => url.includes(endpoint));
};

// ======================
// CSRF Configuration
// ======================
const CSRF_COOKIE_NAME = 'X-CSRF-Token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

// ======================
// In-Memory Token Storage
// ======================
// SECURITY: Access token stored ONLY in memory - not accessible via XSS
// Refresh token is handled automatically via httpOnly cookie
let inMemoryAccessToken = null;
let inMemoryUser = null;
let tokenExpiration = null;
let hasConfirmedSession = readConfirmedSessionFlag();

function readConfirmedSessionFlag() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(CONFIRMED_SESSION_KEY) === '1';
  } catch (error) {
    return false;
  }
}

function setConfirmedSessionFlag(value) {
  hasConfirmedSession = !!value;

  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (hasConfirmedSession) {
      window.sessionStorage.setItem(CONFIRMED_SESSION_KEY, '1');
    } else {
      window.sessionStorage.removeItem(CONFIRMED_SESSION_KEY);
    }
  } catch (error) {
    // Ignore storage write failures and keep in-memory state.
  }
}

/**
 * Token Management - stores access token in memory only
 * Refresh token is now handled automatically via httpOnly cookies
 */
const TokenStorage = {
  /**
   * Get the current access token from memory
   */
  getAccessToken: () => inMemoryAccessToken,
  
  /**
   * Get the current user from memory
   */
  getUser: () => inMemoryUser,
  
  /**
   * Get token expiration time
   */
  getExpiration: () => tokenExpiration,
  
  /**
   * Store access token in memory (NOT localStorage)
   * @param {string} accessToken - The JWT access token
   * @param {Date|string} [expiration] - Token expiration time
   */
  setAccessToken: (accessToken, expiration = null) => {
    inMemoryAccessToken = accessToken;
    if (expiration) {
      tokenExpiration = new Date(expiration);
    }

    if (accessToken) {
      // A valid access token means this tab has a confirmed authenticated session.
      setConfirmedSessionFlag(true);
    }
  },
  
  /**
   * Store user data in memory
   * @param {Object} user - User data
   */
  setUser: (user) => {
    inMemoryUser = user;
    // Also store in localStorage for session persistence across tabs
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  },
  
  /**
   * Store both access token and user
   * Note: refreshToken parameter is ignored - it's handled via httpOnly cookie
   */
  setTokens: (accessToken, _refreshToken, expiration = null) => {
    TokenStorage.setAccessToken(accessToken, expiration);
  },
  
  /**
   * Clear all auth data from memory
   */
  clear: () => {
    inMemoryAccessToken = null;
    inMemoryUser = null;
    tokenExpiration = null;
    localStorage.removeItem('user');
    setConfirmedSessionFlag(false);
  },
  
  /**
   * Check if we have an access token
   * Note: We can't check refresh token as it's in httpOnly cookie
   */
  hasTokens: () => {
    return !!inMemoryAccessToken;
  },

  /**
   * Check if this tab has had a confirmed authenticated session.
   */
  hasConfirmedSession: () => {
    return hasConfirmedSession;
  },
  
  /**
   * Check if token is expired or about to expire (within 30 seconds)
   */
  isTokenExpired: () => {
    if (!tokenExpiration) return true;
    const now = new Date();
    const bufferMs = 30000; // 30 seconds buffer
    return now.getTime() >= tokenExpiration.getTime() - bufferMs;
  },
  
  /**
   * Initialize from localStorage (user data only, for session persistence)
   * Access token cannot be restored as it was only in memory
   */
  initFromStorage: () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        inMemoryUser = JSON.parse(userStr);
      } catch (e) {
        localStorage.removeItem('user');
      }
    }
  },

  // Legacy compatibility - getRefreshToken returns null as it's in httpOnly cookie
  getRefreshToken: () => null,
};

// Initialize user from localStorage
TokenStorage.initFromStorage();

// ======================
// CSRF Token Helper
// ======================
/**
 * Read CSRF token from cookie (set by server)
 * The server sets a readable (non-httpOnly) cookie with the CSRF token
 */
function getCsrfToken() {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Check if request method requires CSRF protection
 */
function requiresCsrf(method) {
  const csrfMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
  return csrfMethods.includes(method?.toUpperCase());
}

function buildSecurityHeaders(method, { includeAuthorization = true } = {}) {
  const headers = {
    'X-Client-Correlation-Id': generateCorrelationId(),
  };

  if (includeAuthorization) {
    const accessToken = TokenStorage.getAccessToken();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  if (requiresCsrf(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  return headers;
}

// ======================
// Axios Instance
// ======================
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: Include cookies for CORS (refresh token + CSRF)
});

// ======================
// Token Refresh Logic
// ======================
let isRefreshing = false;
let refreshSubscribers = [];

// ======================
// Re-Authentication Logic
// ======================
// When refresh fails, instead of hard logout, we show a re-auth modal
let isReAuthPending = false;
let reAuthPromiseResolvers = [];

/**
 * Add request to re-auth queue
 * These requests will be retried after successful re-authentication
 */
const subscribeReAuth = (resolve, reject, originalRequest) => {
  reAuthPromiseResolvers.push({ resolve, reject, originalRequest });
};

/**
 * Notify all queued requests of successful re-auth
 */
const onReAuthSuccess = (accessToken) => {
  isReAuthPending = false;
  const resolvers = [...reAuthPromiseResolvers];
  reAuthPromiseResolvers = [];
  
  resolvers.forEach(({ resolve, originalRequest }) => {
    // Retry the original request with new token
    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
    resolve(apiClient(originalRequest));
  });
};

/**
 * Notify all queued requests of failed re-auth
 */
const onReAuthFailed = (error) => {
  isReAuthPending = false;
  const resolvers = [...reAuthPromiseResolvers];
  reAuthPromiseResolvers = [];
  
  resolvers.forEach(({ reject }) => {
    reject(error);
  });
};

// Listen for re-auth completion events
if (typeof window !== 'undefined') {
  window.addEventListener('auth:reauth-success', (event) => {
    const { accessToken } = event.detail || {};
    if (accessToken) {
      onReAuthSuccess(accessToken);
    }
  });
  
  window.addEventListener('auth:reauth-cancelled', () => {
    onReAuthFailed(new Error('Re-authentication cancelled'));
  });
}

/**
 * Add request to refresh queue
 */
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const isReAuthEligible = () => {
  return TokenStorage.hasConfirmedSession();
};

/**
 * Notify all queued requests with new token
 */
const onTokenRefreshed = (accessToken) => {
  refreshSubscribers.forEach(callback => callback(accessToken));
  refreshSubscribers = [];
};

/**
 * Notify all queued requests of refresh failure
 */
const onRefreshFailed = (error) => {
  refreshSubscribers.forEach(callback => callback(null, error));
  refreshSubscribers = [];
};

/**
 * Attempt to refresh the access token
 * Refresh token is sent automatically via httpOnly cookie
 */
const refreshAccessToken = async () => {
  const accessToken = TokenStorage.getAccessToken();

  if (!accessToken) {
    throw new UnauthorizedError('No access token available for refresh');
  }

  // Use a separate axios request to avoid recursive refresh interception,
  // but still mirror the auth and CSRF headers expected by the backend.
  const response = await axios.post(
    `${API_BASE_URL}${TOKEN_REFRESH_ENDPOINT}`,
    { accessToken }, // Only send expired access token
    {
      withCredentials: true,
      headers: buildSecurityHeaders('POST'),
    }
  );

  const { accessToken: newAccessToken, expiration, user } = response.data;
  
  // Store new tokens in memory
  TokenStorage.setAccessToken(newAccessToken, expiration);
  if (user) TokenStorage.setUser(user);

  return newAccessToken;
};

/**
 * Restore session using only the httpOnly cookie (after page refresh).
 * Unlike refreshAccessToken, this does NOT require an existing access token.
 * @returns {Promise<Object|null>} User data if session was restored, null otherwise
 */
const restoreSession = async () => {
  try {
    // Call session-restore endpoint - it reads refresh token from httpOnly cookie
    const response = await axios.post(
      `${API_BASE_URL}/auth/session-restore`,
      {}, // No body needed - refresh token is in httpOnly cookie
      { withCredentials: true }
    );

    const { accessToken, expiration, user } = response.data;
    
    // Store restored tokens
    TokenStorage.setAccessToken(accessToken, expiration);
    if (user) TokenStorage.setUser(user);

    return user;
  } catch (error) {
    // Session restoration failed - this is expected if no valid session exists
    // Clear any stale user data from storage
    TokenStorage.clear();
    return null;
  }
};

// ======================
// Request Interceptor
// ======================
apiClient.interceptors.request.use(
  (config) => {
    Object.assign(config.headers, buildSecurityHeaders(config.method));

    // For FormData, remove Content-Type so browser sets it with correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ======================
// Response Interceptor
// ======================
apiClient.interceptors.response.use(
  // Success handler - just return the response
  (response) => response,
  
  // Error handler
  async (error) => {
    const originalRequest = error.config;

    // Handle request cancellation gracefully
    if (axios.isCancel(error)) {
      throw new RequestCancelledError();
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh token requests
      if (originalRequest.url === TOKEN_REFRESH_ENDPOINT) {
        TokenStorage.clear();
        dispatchLogoutEvent();
        throw ApiError.fromAxiosError(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken, refreshError) => {
            if (refreshError) {
              reject(refreshError);
            } else if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(apiClient(originalRequest));
            } else {
              reject(ApiError.fromAxiosError(error));
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        isRefreshing = false;
        onTokenRefreshed(newAccessToken);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed(refreshError);
        
        // For auth endpoints (login, register, 2FA, etc.), don't show re-auth modal
        // Just throw the error so it can be displayed as a form error
        if (shouldSkipReAuth(originalRequest.url)) {
          throw ApiError.fromAxiosError(error);
        }

        // Only show re-auth modal if this tab had a confirmed authenticated session.
        // This avoids modal popups from stale cached user data during public-page bootstrap.
        if (!isReAuthEligible()) {
          TokenStorage.clear();
          throw ApiError.fromAxiosError(error);
        }
        
        // Instead of hard logout, trigger re-authentication modal
        // and queue this request to be retried after successful re-auth
        return new Promise((resolve, reject) => {
          // If re-auth is already pending, just queue this request
          if (isReAuthPending) {
            subscribeReAuth(resolve, reject, originalRequest);
            return;
          }
          
          // Start re-auth flow
          isReAuthPending = true;
          subscribeReAuth(resolve, reject, originalRequest);
          
          // Determine the reason for re-auth based on the error
          let reason = 'session_expired';
          const errorMessage = refreshError?.response?.data?.message || refreshError?.message || '';
          if (errorMessage.toLowerCase().includes('disabled')) {
            reason = 'account_disabled';
          } else if (errorMessage.toLowerCase().includes('revoked')) {
            reason = 'session_revoked';
          } else if (errorMessage.toLowerCase().includes('invalid')) {
            reason = 'token_invalid';
          }
          
          // Get cached user email for convenience
          const cachedUser = TokenStorage.getUser();
          const email = cachedUser?.email || '';
          
          // Dispatch re-auth needed event
          // ReAuthContext will listen for this and show the modal
          dispatchReAuthNeededEvent({ email, reason, originalRequest });
        });
      }
    }

    // Handle 403 CSRF errors - these indicate cookies were cleared
    // (CSRF token is stored in a cookie, so if it's missing, session cookies are likely gone too)
    if (error.response?.status === 403 && !originalRequest._retryAfterReAuth) {
      const errorMessage = error.response?.data?.message || error.response?.data?.title || '';
      const isCsrfError = errorMessage.toLowerCase().includes('csrf');
      
      if (isCsrfError) {
        // For auth endpoints (login, register, 2FA, etc.), don't show re-auth modal
        // Just throw the error so it can be displayed as a form error
        if (shouldSkipReAuth(originalRequest.url)) {
          throw ApiError.fromAxiosError(error);
        }

        // CSRF failures should only raise re-auth for previously confirmed sessions.
        if (!isReAuthEligible()) {
          TokenStorage.clear();
          throw ApiError.fromAxiosError(error);
        }
        
        // Mark to avoid infinite retry loops
        originalRequest._retryAfterReAuth = true;
        
        // Clear tokens since CSRF cookie being gone means session cookies are gone
        TokenStorage.clear();
        
        // Trigger re-authentication modal
        return new Promise((resolve, reject) => {
          // If re-auth is already pending, just queue this request
          if (isReAuthPending) {
            subscribeReAuth(resolve, reject, originalRequest);
            return;
          }
          
          // Start re-auth flow
          isReAuthPending = true;
          subscribeReAuth(resolve, reject, originalRequest);
          
          // Get cached user email for convenience (may still be in localStorage)
          const cachedUser = TokenStorage.getUser() || JSON.parse(localStorage.getItem('user') || 'null');
          const email = cachedUser?.email || '';
          
          // Dispatch re-auth needed event with session_expired reason
          // (CSRF token missing effectively means session is invalid)
          dispatchReAuthNeededEvent({ email, reason: 'session_expired', originalRequest });
        });
      }
    }

    // Convert to typed ApiError and rethrow
    throw ApiError.fromAxiosError(error);
  }
);

// ======================
// Helper Functions
// ======================

/**
 * Generate a unique correlation ID for request tracking
 */
function generateCorrelationId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Dispatch logout event for AuthContext to handle
 */
function dispatchLogoutEvent() {
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

/**
 * Dispatch re-auth needed event for ReAuthContext to handle
 * This shows the re-auth modal instead of redirecting to login
 */
function dispatchReAuthNeededEvent({ email, reason, originalRequest }) {
  window.dispatchEvent(new CustomEvent('auth:reauth-needed', {
    detail: { email, reason, originalRequest }
  }));
}

/**
 * Notify that re-auth was successful (called by ReAuthContext after successful login)
 * This will trigger retry of all queued requests
 */
function notifyReAuthSuccess(accessToken) {
  window.dispatchEvent(new CustomEvent('auth:reauth-success', {
    detail: { accessToken }
  }));
}

/**
 * Notify that re-auth was cancelled (called by ReAuthContext when user cancels)
 * This will reject all queued requests
 */
function notifyReAuthCancelled() {
  window.dispatchEvent(new CustomEvent('auth:reauth-cancelled'));
}

// ======================
// Notification Event System
// ======================
// These events are dispatched for NotificationContext to handle

/**
 * Options to control notification behavior
 * @typedef {Object} RequestOptions
 * @property {boolean} [suppressNotification=false] - Don't show error notification
 * @property {boolean} [suppressSuccessNotification=true] - Don't show success notification
 * @property {string} [successMessage] - Custom success message to show
 * @property {Function} [onUploadProgress] - Upload progress callback
 * @property {AbortSignal} [signal] - AbortController signal for cancellation
 */

/**
 * Dispatch API error event for notification system
 */
function dispatchApiError(error, options = {}) {
  if (!options.suppressNotification) {
    window.dispatchEvent(new CustomEvent('api:error', { 
      detail: { error, options }
    }));
  }
}

/**
 * Dispatch API success event for notification system
 */
function dispatchApiSuccess(message, options = {}) {
  if (!options.suppressSuccessNotification && message) {
    window.dispatchEvent(new CustomEvent('api:success', { 
      detail: { message, options }
    }));
  }
}

// ======================
// High-Level API Methods
// ======================

/**
 * GET request
 * @param {string} url - Endpoint URL
 * @param {Object} [params] - Query parameters
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<any>} Response data
 */
async function get(url, params = {}, options = {}) {
  try {
    const response = await apiClient.get(url, { 
      params,
      signal: options.signal,
    });
    return response.data;
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * POST request
 * @param {string} url - Endpoint URL
 * @param {Object} [data] - Request body
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<any>} Response data
 */
async function post(url, data = {}, options = {}) {
  try {
    const response = await apiClient.post(url, data, {
      signal: options.signal,
      onUploadProgress: options.onUploadProgress,
    });
    dispatchApiSuccess(options.successMessage, options);
    return response.data;
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * PUT request
 * @param {string} url - Endpoint URL
 * @param {Object} [data] - Request body
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<any>} Response data
 */
async function put(url, data = {}, options = {}) {
  try {
    const response = await apiClient.put(url, data, {
      signal: options.signal,
    });
    dispatchApiSuccess(options.successMessage, options);
    return response.data;
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * PATCH request
 * @param {string} url - Endpoint URL
 * @param {Object} [data] - Request body
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<any>} Response data
 */
async function patch(url, data = {}, options = {}) {
  try {
    const response = await apiClient.patch(url, data, {
      signal: options.signal,
    });
    dispatchApiSuccess(options.successMessage, options);
    return response.data;
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * DELETE request
 * @param {string} url - Endpoint URL
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<any>} Response data (null for 204)
 */
async function del(url, options = {}) {
  try {
    const response = await apiClient.delete(url, {
      signal: options.signal,
    });
    dispatchApiSuccess(options.successMessage, options);
    // Return null for 204 No Content
    return response.status === 204 ? null : response.data;
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * Upload file(s) with progress tracking
 * @param {string} url - Endpoint URL
 * @param {FormData} formData - Form data with file(s)
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<any>} Response data
 */
async function upload(url, formData, options = {}) {
  try {
    const response = await apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: options.signal,
      onUploadProgress: options.onUploadProgress,
    });
    dispatchApiSuccess(options.successMessage, options);
    return response.data;
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * Upload file(s) with PUT method (for updates) with progress tracking
 * @param {string} url - Endpoint URL
 * @param {FormData} formData - Form data with file(s)
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<any>} Response data
 */
async function uploadPut(url, formData, options = {}) {
  try {
    const response = await apiClient.put(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: options.signal,
      onUploadProgress: options.onUploadProgress,
    });
    dispatchApiSuccess(options.successMessage, options);
    return response.data;
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * Download file as blob
 * @param {string} url - Endpoint URL
 * @param {RequestOptions} [options] - Request options
 * @returns {Promise<{blob: Blob, filename: string}>} Blob and filename
 */
async function download(url, options = {}) {
  try {
    const response = await apiClient.get(url, {
      responseType: 'blob',
      signal: options.signal,
    });
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    return { blob: response.data, filename };
  } catch (error) {
    dispatchApiError(error, options);
    throw error;
  }
}

/**
 * Create an AbortController for request cancellation
 * @returns {{controller: AbortController, signal: AbortSignal}}
 */
function createCancellation() {
  const controller = new AbortController();
  return {
    controller,
    signal: controller.signal,
    cancel: () => controller.abort(),
  };
}

// ======================
// Exports
// ======================
export {
  apiClient,
  TokenStorage,
  restoreSession,
  get,
  post,
  put,
  patch,
  del as delete,
  upload,
  uploadPut,
  download,
  createCancellation,
  API_BASE_URL,
  // Re-auth helpers
  notifyReAuthSuccess,
  notifyReAuthCancelled,
};

// Default export as object for convenience
const api = {
  client: apiClient,
  tokens: TokenStorage,
  restoreSession,
  get,
  post,
  put,
  patch,
  delete: del,
  upload,
  uploadPut,
  download,
  createCancellation,
  baseUrl: API_BASE_URL,
  // Re-auth helpers
  notifyReAuthSuccess,
  notifyReAuthCancelled,
};

export default api;
